import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/feature/AppShell";
import ProgressBar from "@/components/base/ProgressBar";
import ScrollFade from "@/components/base/ScrollFade";
import { courses as mockCourses } from "@/mocks/vivelo";
import { useCourseProgress } from "@/hooks/useCourseProgress";

const tabs = [
  { id: "reco", label: "Recomendados para ti" },
  { id: "progress", label: "En progreso" },
  { id: "done", label: "Completados" },
  { id: "all", label: "Todos los cursos" },
];

const tagStyles: Record<string, string> = {
  primary: "bg-primary-500/15 text-primary-700",
  secondary: "bg-secondary-500/15 text-secondary-700",
  warning: "bg-amber-100 text-amber-700",
};

const ctaStyles: Record<string, string> = {
  primary: "bg-primary-500 text-background-950 hover:bg-primary-600",
  secondary: "bg-secondary-500 text-white hover:bg-secondary-600",
  accent: "bg-accent-500 text-white hover:bg-accent-600",
  "outline-primary": "bg-transparent text-primary-700 border border-primary-500 hover:bg-primary-500/10",
  "outline-warning": "bg-transparent text-amber-700 border border-amber-400 hover:bg-amber-50",
};

const iconBg: Record<string, string> = {
  primary: "bg-primary-500/15 text-primary-600",
  secondary: "bg-secondary-500/15 text-secondary-600",
  accent: "bg-accent-500/15 text-accent-600",
  warning: "bg-amber-100 text-amber-600",
};

export default function Cursos() {
  const navigate = useNavigate();
  const [active, setActive] = useState("reco");
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const [query, setQuery] = useState("");
  const { progressByCourseId, loading } = useCourseProgress();

  // Progreso real por curso desde Supabase (tabla course_progress, calculada
  // automáticamente a partir de las lecciones vistas). El catálogo (título,
  // descripción, lecciones, nivel, etc.) siempre viene del mock -- eso es
  // contenido del curso, no algo que vivamos en la base de progreso.
  // Mientras carga mostramos el catálogo de muestra para no parpadear a 0%.
  const courses = useMemo(() => {
    if (loading) return mockCourses;
    return mockCourses.map((c) => {
      const realProgress = progressByCourseId.get(c.id) ?? 0;
      return {
        ...c,
        progress: realProgress,
        cta: realProgress >= 100 ? "Repasar" : realProgress > 0 ? "Continuar" : "Ver curso",
      };
    });
  }, [progressByCourseId, loading]);

  const filtered = useMemo(() => {
    const base =
      active === "progress"
        ? courses.filter((c) => c.progress > 0 && c.progress < 100)
        : active === "done"
        ? courses.filter((c) => c.progress === 100)
        : courses;
    if (!query.trim()) return base;
    const q = query.trim().toLowerCase();
    return base.filter((c) => c.title.toLowerCase().includes(q) || c.description.toLowerCase().includes(q));
  }, [active, query, courses]);

  return (
    <AppShell headerVariant="onDark" showSearch>
      <section className="bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
          {/* Title */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-foreground-950">Mis Cursos</h1>
              <p className="text-foreground-600 text-sm mt-1 max-w-xl">
                Aprende a tu ritmo con rutas diseñadas para tu perfil y transforma tu vida.
              </p>
            </div>
            <button className="inline-flex items-center gap-2 self-start md:self-auto bg-primary-500/10 text-primary-700 border border-primary-500/30 rounded-full px-4 py-2.5 text-sm font-semibold hover:bg-primary-500/20 cursor-pointer whitespace-nowrap">
              <i className="ri-medal-line"></i> Mis certificados <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>

          {/* Mobile search */}
          <div className="md:hidden mt-4">
            <div className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-full px-4 py-2">
              <i className="ri-search-line text-foreground-400 text-sm"></i>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                type="text"
                placeholder="Buscar cursos..."
                className="w-full bg-transparent outline-none text-sm text-foreground-950"
              />
            </div>
          </div>

          {/* Tabs */}
          <ScrollFade
            containerClassName="mt-6 flex items-center gap-1 border-b border-background-200 overflow-x-auto vv-scrollbar-hidden snap-x snap-mandatory scroll-px-1"
            ariaLabel="Categorías de cursos"
          >
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                className={[
                  "snap-start px-4 py-2.5 text-sm font-semibold whitespace-nowrap cursor-pointer -mb-px border-b-2 transition-colors",
                  active === t.id
                    ? "border-primary-500 text-primary-700"
                    : "border-transparent text-foreground-600 hover:text-foreground-900",
                ].join(" ")}
              >
                {t.label}
              </button>
            ))}
          </ScrollFade>

          {/* AI recommendation banner */}
          <div className="mt-6 relative overflow-hidden rounded-2xl bg-background-950 text-white p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4">
            <div className="absolute inset-0 opacity-80 pointer-events-none"
              style={{ background: "radial-gradient(500px 220px at 10% 40%, oklch(var(--accent-700) / 0.55), transparent 60%), radial-gradient(500px 220px at 100% 60%, oklch(var(--secondary-700) / 0.4), transparent 60%)" }}></div>
            <div className="relative w-16 h-16 rounded-full bg-background-900 border border-primary-400/40 flex items-center justify-center">
              <i className="ri-robot-2-fill text-3xl text-primary-400"></i>
            </div>
            <div className="relative flex-1">
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary-300">
                <i className="ri-sparkling-2-fill"></i> Recomendación IA
              </div>
              <div className="font-heading text-lg md:text-xl font-bold mt-1">
                Hemos seleccionado estos cursos especialmente <span className="vv-gradient-text">para ti</span>.
              </div>
              <div className="text-xs text-foreground-300 mt-1">
                Basado en tu perfil Estratega Analítico y tu ruta actual.
              </div>
            </div>
            <div className="relative w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center">
              <i className="ri-check-double-line text-2xl text-background-950"></i>
            </div>
          </div>

          {/* Courses grid */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            {filtered.map((c) => (
              <article key={c.id} className="bg-background-100 border border-background-200 rounded-2xl p-5 flex gap-4">
                <span className={`w-14 h-14 flex-shrink-0 rounded-2xl flex items-center justify-center ${iconBg[c.tagColor] ?? iconBg.primary}`}>
                  <i className={`${c.icon} text-2xl`}></i>
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full ${tagStyles[c.tagColor] ?? tagStyles.primary}`}>
                        {c.tag}
                      </span>
                      <h3 className="font-heading font-bold text-foreground-950 mt-1">{c.title}</h3>
                      <p className="text-sm text-foreground-600 mt-1">{c.description}</p>
                    </div>
                    <button
                      onClick={() => setSaved((s) => ({ ...s, [c.id]: !s[c.id] }))}
                      aria-label="Guardar curso"
                      className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-background-100 cursor-pointer"
                    >
                      <i className={`${saved[c.id] ? "ri-bookmark-fill text-primary-600" : "ri-bookmark-line text-foreground-500"} text-lg`}></i>
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-600">
                    <span className="inline-flex items-center gap-1"><i className="ri-file-list-2-line"></i> {c.lessons} lecciones</span>
                    <span className="inline-flex items-center gap-1"><i className="ri-time-line"></i> {c.minutes} min</span>
                    <span className="inline-flex items-center gap-1"><i className="ri-bar-chart-2-line"></i> {c.level}</span>
                  </div>
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span>Progreso</span>
                      <span className="font-semibold text-foreground-800">{c.progress}%</span>
                    </div>
                    <div className="mt-1"><ProgressBar value={c.progress} color={c.ctaColor.includes("secondary") ? "secondary" : c.ctaColor.includes("accent") ? "accent" : "primary"} /></div>
                  </div>
                  <button
                    onClick={() => navigate(`/app/cursos/${c.id}`)}
                    className={`mt-4 w-full py-2.5 rounded-xl text-sm font-heading font-bold inline-flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap ${ctaStyles[c.ctaColor]}`}
                  >
                    {c.cta} <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </article>
            ))}
          </div>

          {/* Masterclass CTA */}
          <div className="mt-8 relative overflow-hidden rounded-2xl bg-background-100 border border-background-200 p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4">
            <span className="w-14 h-14 rounded-2xl bg-accent-500/15 flex items-center justify-center">
              <i className="ri-calendar-event-line text-2xl text-accent-600"></i>
            </span>
            <div className="flex-1">
              <h4 className="font-heading font-bold text-foreground-950 text-lg">¿Quieres avanzar más rápido?</h4>
              <p className="text-sm text-foreground-600">
                Asiste a nuestras <span className="font-semibold text-foreground-800">masterclass</span> y sesiones en vivo para profundizar en cada tema y resolver tus dudas.
              </p>
            </div>
            <button
              onClick={() => navigate("/app/reuniones")}
              className="self-start md:self-auto inline-flex items-center gap-2 bg-primary-500 text-background-950 rounded-full px-5 py-2.5 font-heading font-bold text-sm hover:bg-primary-600 cursor-pointer whitespace-nowrap"
            >
              Ver próximas sesiones <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </section>
    </AppShell>
  );
}
